import PocketBase, { BaseAuthStore } from 'pocketbase'

/** An auth store that lives in memory only; BaseAuthStore is abstract in this SDK. */
class MemoryAuthStore extends BaseAuthStore {}
import { getClientSession } from '../api/clientAuth'
import { isPreview, previewPocketBaseBase } from '../api/clientPortal/previewMode'

/**
 * The PocketBase client the portal reads through.
 *
 * While an agent is previewing, it points at the preview proxy instead of
 * PocketBase. The SDK builds `${base}/api/collections/...` either way, so
 * every existing call site is unchanged; the proxy forwards the read under a
 * short-lived impersonation token and refuses anything that is not a read.
 */
export function getClientPb(): PocketBase {
  const session = getClientSession()
  if (!session) throw new Error('Client not authenticated')
  const base = isPreview()
    ? previewPocketBaseBase()
    : (import.meta.env.VITE_POCKETBASE_URL as string) || 'http://localhost:8090'
  /*
   * Its own storage key, and this matters more than it looks.
   *
   * `new PocketBase(base)` builds a LocalAuthStore on PocketBase's default
   * key, which is `pocketbase_auth` - the very key the agency app keeps the
   * AGENT's session under. So saving the portal session here overwrote it.
   * An agent who used "See what they see" came back with their own token
   * still in place but the athlete's portal record as their user model, and
   * every screen that scopes by user id then matched nothing: Compliance
   * showed "All 2" over an empty table telling them to add their first
   * filing, while two agent registration renewals sat 74 days overdue.
   *
   * The portal's real session lives under CLIENT_AUTH_KEY in api/clientAuth,
   * so nothing needs this store to persist at all; it only has to carry the
   * token for the requests this instance makes. So it lives in memory.
   *
   * It used to be a LocalAuthStore on its own key, 'reppr_portal_pb_auth',
   * which nothing ever cleared: the athlete's token stayed on the device after
   * they signed out, and during an agent's "See what they see" preview it was
   * the AGENT's token, left on disk past the preview and the agent's own
   * sign-out (Oct 2026 audit). clearClientAuth removes any copy left behind.
   */
  const clientPb = new PocketBase(base, new MemoryAuthStore())
  clientPb.autoCancellation(false)
  clientPb.authStore.save(session.token, session.user as any)
  return clientPb
}
